import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * WorkflowService - Handles workflow/wizard management
 *
 * Workflows (wizards) are guided step-by-step processes for WMS operations.
 * They are stored as tools with toolCategory = 'WIZARD'.
 */
class WorkflowService {

  // ==================== Tool/Wizard CRUD ====================

  /**
   * Get all tools (filtered to wizards only)
   * @returns {Promise<Array>} Array of wizard tool objects
   */
  static async getTools() {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools?category=WIZARD`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tools:', error);
      throw error;
    }
  }

  /**
   * Get all workflows/wizards
   * @returns {Promise<Array>} Array of workflow objects
   */
  static async getWorkflows() {
    return this.getTools();
  }

  /**
   * Get a specific tool by ID
   * @param {string} toolId - The tool ID
   * @returns {Promise<Object>} Tool object
   */
  static async getTool(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(toolId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tool:', error);
      throw error;
    }
  }

  /**
   * Create a new tool/wizard
   * @param {Object} toolData - Tool data
   * @returns {Promise<Object>} Created tool object
   */
  static async createTool(toolData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(toolData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tool:', error);
      throw error;
    }
  }

  /**
   * Update a tool/wizard
   * @param {string} toolId - The tool ID
   * @param {Object} toolData - Updated tool data
   * @returns {Promise<Object>} Updated tool object
   */
  static async updateTool(toolId, toolData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(toolId)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(toolData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tool:', error);
      throw error;
    }
  }

  /**
   * Delete a tool/wizard
   * @param {string} toolId - The tool ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteTool(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(toolId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting tool:', error);
      throw error;
    }
  }

  // ==================== Wizard Steps ====================

  /**
   * Get steps for a wizard
   * @param {string} toolId - The parent wizard tool ID
   * @returns {Promise<Array>} Array of step objects
   */
  static async getWizardSteps(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(toolId)}/steps`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting wizard steps:', error);
      throw error;
    }
  }

  /**
   * Create a wizard step
   * @param {string} parentToolId - The parent wizard tool ID
   * @param {Object} stepData - Step data
   * @returns {Promise<Object>} Created step object
   */
  static async createWizardStep(parentToolId, stepData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(parentToolId)}/steps`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(stepData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating wizard step:', error);
      throw error;
    }
  }

  /**
   * Update a wizard step
   * @param {string} stepId - The step ID
   * @param {Object} stepData - Updated step data
   * @returns {Promise<Object>} Updated step object
   */
  static async updateWizardStep(stepId, stepData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/steps/${encodeURIComponent(stepId)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(stepData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating wizard step:', error);
      throw error;
    }
  }

  /**
   * Delete a wizard step
   * @param {string} stepId - The step ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteWizardStep(stepId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/steps/${encodeURIComponent(stepId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting wizard step:', error);
      throw error;
    }
  }

  /**
   * Reorder wizard steps
   * @param {string} parentToolId - The parent wizard tool ID
   * @param {Array<string>} stepIds - Array of step IDs in new order
   * @returns {Promise<Object>} Reorder result
   */
  static async reorderWizardSteps(parentToolId, stepIds) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tools/${encodeURIComponent(parentToolId)}/steps/reorder`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ stepIds }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error reordering wizard steps:', error);
      throw error;
    }
  }
}

export default WorkflowService;
